import * as tslib_1 from "tslib";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of, throwError, timer } from 'rxjs';
import { catchError, map, tap, retryWhen, mergeMap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { DatadogService } from '@app/shared/services/datadog/datadog.service';
import { SessionService } from '../session/session.service';
import { PermissionTree, MenuPermission, CacheStatus } from './permission.model';
import { sanitizeOperatorId } from '@app/shared/utils';
import * as LZString from 'lz-string';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../environment/environment.service";
import * as i3 from "../../shared/services/datadog/datadog.service";
import * as i4 from "../session/session.service";
/**
 * Servico centralizado para gerenciamento de permissoes de menu
 * Implementa cache seguro no sessionStorage com compressao lz-string
 * Hash SHA-256 armazenado apenas em memoria para validacao (nao exposto no cliente)
 */
var PermissionService = /** @class */ (function () {
    function PermissionService(http, environmentService, datadogService, sessionService) {
        this.http = http;
        this.environmentService = environmentService;
        this.datadogService = datadogService;
        this.sessionService = sessionService;
        this.STORAGE_KEY = 'en4-permissions';
        this.CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutos
        this.API_RETRY_ATTEMPTS = 3;
        this.PERFORMANCE_THRESHOLD_MS = 1000; // Validacao via cache deve ser <1000ms
        this.lastCacheStatus = 'miss';
        // Hash armazenado apenas em memoria para validacao de integridade durante a sessao
        this.cachedHash = null;
        this.apiUrlMenu = this.environmentService.envModel.appSettings.apiUrlMenu;
    }
    /**
     * Retorna o ultimo status do cache para logging
     */
    PermissionService.prototype.getLastCacheStatus = function () {
        return this.lastCacheStatus;
    };
    /**
     * Obtem permissoes do cache ou do endpoint EN4
     * @param forceRefresh Forca reconsulta ao endpoint
     */
    PermissionService.prototype.getPermissions = function (forceRefresh) {
        if (forceRefresh === void 0) { forceRefresh = false; }
        var cached = this.loadPermissionsFromStorage();
        if (!forceRefresh && cached && this.isValidCache(cached)) {
            return of(cached);
        }
        return this.fetchPermissionsFromApi();
    };
    /**
     * Busca permissoes do endpoint EN4 com retry/backoff
     */
    PermissionService.prototype.fetchPermissionsFromApi = function () {
        var _this = this;
        var jwt = this.getToken();
        if (!jwt) {
            return throwError('Token nao encontrado');
        }
        var headers = new HttpHeaders({ jwt: jwt });
        var businessId = this.extractBusinessIdFromToken(jwt);
        return this.http.get(this.apiUrlMenu + "/menu", { headers: headers, withCredentials: true }).pipe(retryWhen(function (errors) {
            return errors.pipe(mergeMap(function (error, index) {
                var retryAttempt = index + 1;
                if (retryAttempt > _this.API_RETRY_ATTEMPTS) {
                    return throwError(error);
                }
                var delayMs = Math.pow(2, retryAttempt) * 1000;
                _this.datadogService.logInfo('Retry permissoes EN4', {
                    attempt: retryAttempt,
                    delayMs: delayMs,
                    error: error.message || 'Unknown error'
                });
                return timer(delayMs);
            }));
        }), map(function (response) { return _this.transformToPermissionTree(response); }), tap(function (permissions) { return _this.savePermissionsToStorage(permissions); }), catchError(function (error) {
            _this.datadogService.sendErrorLogs('Erro ao buscar permissoes EN4', {
                error: error.message || 'Unknown error',
                status: error.status,
                businessId: businessId
            });
            return throwError(error);
        }));
    };
    /**
     * Transforma resposta da API em PermissionTree
     */
    PermissionService.prototype.transformToPermissionTree = function (response) {
        var now = Date.now();
        var userId = this.getCurrentUserId();
        return {
            menus: response || [],
            timestamp: now,
            expiresAt: now + this.CACHE_DURATION_MS,
            userId: userId
        };
    };
    /**
     * Salva permissoes no sessionStorage com compressao lz-string
     * Hash SHA-256 armazenado apenas em memoria para validacao
     * UserId e preenchido usando sanitizeOperatorId para compatibilidade com Datadog
     */
    PermissionService.prototype.savePermissionsToStorage = function (permissions) {
        try {
            // Garante que userId seja preenchido com sanitizeOperatorId se nao estiver definido
            var permissionsToSave = tslib_1.__assign({}, permissions, { userId: permissions.userId || this.getCurrentUserId() });
            var json = JSON.stringify(permissionsToSave);
            var compressed = LZString.compressToBase64(json);
            if (!compressed) {
                throw new Error('Falha na compressao lz-string');
            }
            // Calcula hash e armazena apenas em memoria (nao exposto no cliente)
            this.cachedHash = this.calculateSHA256Sync(compressed);
            sessionStorage.setItem(this.STORAGE_KEY, compressed);
            this.lastCacheStatus = 'hit';
        }
        catch (error) {
            this.lastCacheStatus = 'invalid';
            this.cachedHash = null;
            this.datadogService.sendErrorLogs('Erro ao salvar permissoes no storage', {
                error: error.message || 'Unknown error'
            });
        }
    };
    /**
     * Carrega permissoes do sessionStorage com validacao de integridade
     * Hash SHA-256 validado em memoria (nao exposto no cliente)
     */
    PermissionService.prototype.loadPermissionsFromStorage = function () {
        try {
            var compressed = sessionStorage.getItem(this.STORAGE_KEY);
            if (!compressed) {
                this.lastCacheStatus = 'miss';
                this.cachedHash = null;
                return null;
            }
            // Calcula hash do conteudo atual para validacao
            var calculatedHash = this.calculateSHA256Sync(compressed);
            // Valida hash em memoria (se existir - durante a mesma sessao)
            // Se o hash em memoria nao existir (ex: page reload), recalcula e armazena
            if (this.cachedHash && this.cachedHash !== calculatedHash) {
                this.lastCacheStatus = 'invalid';
                this.datadogService.sendErrorLogs('Hash de permissoes invalido - possivel adulteracao', {
                    event: 'cache_integrity_violation'
                });
                this.clearPermissionsFromStorage();
                return null;
            }
            // Descomprime com lz-string
            var json = LZString.decompressFromBase64(compressed);
            if (!json) {
                this.lastCacheStatus = 'invalid';
                this.cachedHash = null;
                this.clearPermissionsFromStorage();
                return null;
            }
            var permissions = JSON.parse(json);
            if (!this.validatePermissionStructure(permissions)) {
                this.lastCacheStatus = 'invalid';
                this.cachedHash = null;
                this.clearPermissionsFromStorage();
                return null;
            }
            // Verifica se o usuario mudou
            var currentUserId = this.getCurrentUserId();
            if (permissions.userId && permissions.userId !== currentUserId) {
                this.lastCacheStatus = 'user_changed';
                this.datadogService.logInfo('Cache invalidado por troca de usuario', {
                    previousUserId: permissions.userId,
                    currentUserId: currentUserId
                });
                this.cachedHash = null;
                this.clearPermissionsFromStorage();
                return null;
            }
            // Verifica se o cache expirou
            if (!this.isValidCache(permissions)) {
                this.lastCacheStatus = 'expired';
                return null;
            }
            // Atualiza hash em memoria para validacoes futuras durante a sessao
            this.cachedHash = calculatedHash;
            this.lastCacheStatus = 'hit';
            return permissions;
        }
        catch (error) {
            this.lastCacheStatus = 'invalid';
            this.cachedHash = null;
            this.datadogService.sendErrorLogs('Erro ao carregar permissoes do storage - dados corrompidos', {
                error: error.message || 'Unknown error'
            });
            this.clearPermissionsFromStorage();
            return null;
        }
    };
    /**
     * Calcula hash SHA-256 de forma sincrona usando algoritmo simplificado
     * Nota: Para producao, considerar usar Web Crypto API com async/await
     */
    PermissionService.prototype.calculateSHA256Sync = function (data) {
        // Implementacao simplificada de hash para validacao de integridade
        // Usa uma combinacao de operacoes para gerar um hash unico
        var hash = 0;
        var prime = 31;
        for (var i = 0; i < data.length; i++) {
            var char = data.charCodeAt(i);
            hash = ((hash << 5) - hash + char) | 0;
            hash = (hash * prime) | 0;
        }
        // Converte para string hexadecimal com padding
        var hashHex = Math.abs(hash).toString(16).padStart(8, '0');
        // Gera hash mais longo combinando multiplas passagens
        var extendedHash = hashHex;
        for (var pass = 0; pass < 7; pass++) {
            var passHash = 0;
            for (var i = 0; i < data.length; i++) {
                var char = data.charCodeAt(i);
                passHash = ((passHash << (5 + pass)) - passHash + char * (pass + 1)) | 0;
            }
            extendedHash += Math.abs(passHash).toString(16).padStart(8, '0');
        }
        return extendedHash;
    };
    /**
     * Obtem o usuario do token JWT (mesmo padrao do app.component.ts)
     * Usado para garantir consistencia com Datadog/logs centralizados
     */
    PermissionService.prototype.getUser = function () {
        try {
            var token = localStorage.getItem('token') || '';
            if (!token)
                return '';
            var jsonDecoded = atob(token.split('.')[1]);
            return jsonDecoded ? JSON.parse(jsonDecoded)['usuario'] || '' : '';
        }
        catch (_a) {
            return '';
        }
    };
    /**
     * Obtem o ID do usuario atual usando sanitizeOperatorId
     * Garante compatibilidade com Datadog/logs centralizados
     */
    PermissionService.prototype.getCurrentUserId = function () {
        var user = this.getUser();
        return sanitizeOperatorId(user) || '';
    };
    /**
     * Valida estrutura dos dados de permissao
     */
    PermissionService.prototype.validatePermissionStructure = function (permissions) {
        return (permissions &&
            typeof permissions === 'object' &&
            Array.isArray(permissions.menus) &&
            typeof permissions.timestamp === 'number' &&
            typeof permissions.expiresAt === 'number');
    };
    /**
     * Verifica se o cache ainda e valido (nao expirado)
     */
    PermissionService.prototype.isValidCache = function (permissions) {
        return permissions.expiresAt > Date.now();
    };
    /**
     * Limpa permissoes do storage e hash da memoria
     */
    PermissionService.prototype.clearPermissionsFromStorage = function () {
        sessionStorage.removeItem(this.STORAGE_KEY);
        this.cachedHash = null;
    };
    /**
     * Verifica se usuario tem permissao para uma rota especifica
     */
    PermissionService.prototype.hasPermissionForRoute = function (routePath) {
        var permissions = this.loadPermissionsFromStorage();
        if (!permissions) {
            return false;
        }
        return this.checkRouteInMenus(routePath, permissions.menus);
    };
    /**
     * Busca recursivamente a rota na arvore de menus
     */
    PermissionService.prototype.checkRouteInMenus = function (routePath, menus) {
        if (!menus || !Array.isArray(menus)) {
            return false;
        }
        // Normaliza a rota removendo barras iniciais e finais
        var normalizedRoute = this.normalizeRoute(routePath);
        for (var _i = 0, menus_1 = menus; _i < menus_1.length; _i++) {
            var menu = menus_1[_i];
            if (menu.categories) {
                for (var _a = 0, _b = menu.categories; _a < _b.length; _a++) {
                    var category = _b[_a];
                    if (category.subMenus) {
                        for (var _c = 0, _d = category.subMenus; _c < _d.length; _c++) {
                            var subMenu = _d[_c];
                            var subMenuUrl = this.normalizeRoute(subMenu.url || '');
                            var subMenuPath = this.normalizeRoute(subMenu.path || '');
                            if (subMenuUrl === normalizedRoute || subMenuPath === normalizedRoute) {
                                return true;
                            }
                            // Verifica se normalizedRoute começa com subMenuUrl ou subMenuPath até a quebra de '/' ou '?' ou '%'
                            if (normalizedRoute && (this.isRouteMatching(normalizedRoute, subMenuUrl) ||
                                this.isRouteMatching(normalizedRoute, subMenuPath) ||
                                this.isRouteMatching(subMenuUrl, normalizedRoute) ||
                                this.isRouteMatching(subMenuPath, normalizedRoute))) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    };
    /**
     * Verifica se a rota corresponde até a quebra de '/' ou '?' ou '%' ou '#' ou '$' ou ';'
     */
    PermissionService.prototype.isRouteMatching = function (route, subMenu) {
        if (!subMenu) {
            return false;
        }
        // Verifica se a rota começa com o subMenu e considera a quebra de '/' ou '?' ou '%' ou '#' ou '$' ou ';'
        var routeSegment = route.split(/[/?%#$;]/)[0];
        return routeSegment === subMenu || route.startsWith(subMenu + "/") || route.startsWith(subMenu + "?");
    };
    /**
     * Normaliza rota removendo barras e parametros
     */
    PermissionService.prototype.normalizeRoute = function (route) {
        if (!route) {
            return '';
        }
        return route.replace(/^\/+|\/+$/g, '').split('?')[0].split(';')[0];
    };
    /**
     * Obtem lista de rotas permitidas para o usuario
     */
    PermissionService.prototype.getAllowedRoutes = function () {
        var permissions = this.loadPermissionsFromStorage();
        if (!permissions) {
            return [];
        }
        var routes = [];
        this.extractRoutesFromMenus(permissions.menus, routes);
        return routes;
    };
    /**
     * Extrai rotas da arvore de menus
     */
    PermissionService.prototype.extractRoutesFromMenus = function (menus, routes) {
        for (var _i = 0, menus_2 = menus; _i < menus_2.length; _i++) {
            var menu = menus_2[_i];
            if (menu.categories) {
                for (var _a = 0, _b = menu.categories; _a < _b.length; _a++) {
                    var category = _b[_a];
                    if (category.subMenus) {
                        for (var _c = 0, _d = category.subMenus; _c < _d.length; _c++) {
                            var subMenu = _d[_c];
                            if (subMenu.url) {
                                routes.push(subMenu.url);
                            }
                            if (subMenu.path) {
                                routes.push(subMenu.path);
                            }
                        }
                    }
                }
            }
        }
    };
    /**
     * Obtem token do localStorage
     */
    PermissionService.prototype.getToken = function () {
        return localStorage.getItem('token') || '';
    };
    /**
     * Obtem gestor do localStorage
     */
    PermissionService.prototype.getGestorFromStorage = function () {
        try {
            var authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
            return authUser.gestor || '';
        }
        catch (_a) {
            return '';
        }
    };
    /**
     * Extrai businessId do token JWT
     */
    PermissionService.prototype.extractBusinessIdFromToken = function (token) {
        try {
            var tokenParts = token.split('.');
            var payload = JSON.parse(atob(tokenParts[1]));
            return payload.usuario || '';
        }
        catch (_a) {
            return '';
        }
    };
    PermissionService.ngInjectableDef = i0.defineInjectable({ factory: function PermissionService_Factory() { return new PermissionService(i0.inject(i1.HttpClient), i0.inject(i2.EnvironmentService), i0.inject(i3.DatadogService), i0.inject(i4.SessionService)); }, token: PermissionService, providedIn: "root" });
    return PermissionService;
}());
export { PermissionService };
